import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { songSchemas } from '@/api/songs/song.schema';
import { Content } from '@/components/ui/content';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useSong, useUpdateSong } from '@/services/songs';
import type { ValidationError } from '@/types/api/validation-error';
import { SongForm, type SongFormSchema } from '../+components/song-form';

export const Route = createFileRoute('/_app/songs/$id/')({
  head: () => meta({ label: 'Editar cifra' }),
  component: RouteComponent,
});

function RouteComponent() {
  const router = useRouter();
  const { id } = Route.useParams();
  const { data } = useSong({ id });
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useUpdateSong();

  const form = useForm<SongFormSchema>({
    resolver: zodResolver(songSchemas.create) as any,
    shouldUseNativeValidation: false,
    values: data as unknown as SongFormSchema,
  });

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error?.status === 422) {
      const { errors } = error.value as unknown as ValidationError<SongFormSchema>;

      if (errors) {
        for (const [key, message] of Object.entries(errors)) {
          form.setError(key as keyof SongFormSchema, { message: message as string });
        }
      }
    }
  }, [error, form]);

  return (
    <Content
      breadcrumbs={{
        icon: 'music',
        title: data?.title ?? 'Editar cifra',
        items: [
          { label: 'Cifras', route: '/songs' },
          { label: 'Edição', active: true },
        ],
      }}
    >
      <SongForm
        form={form}
        isSubmitting={isSubmitting}
        onSubmit={(values) => save({ id, data: values } as any)}
        onCancel={() => router.back()}
      />
    </Content>
  );
}
